import { useCallback, useEffect, useMemo, useState } from "react";
import SBINModal from "../modals/SBINModal";
import {
  assignTrainingFromFeedback,
  createStaffCoachingNote,
  getStaffClientSatisfaction,
} from "../../api/staffs";
import { listTrainingModules } from "../../api/training";
import { formatDateTimeInTimeZone } from "../../utils/datetime";
import { getSalonTimezone } from "../../utils/salonTimezone";
import { useAuth } from "../../context/AuthContext";

export default function StaffClientSatisfactionCard({
  staffId,
  token,
  cardClass = "rounded-2xl border border-slate-200 bg-white shadow-sm p-6",
}) {
  const { currentSalon } = useAuth();
  const salonTimezone = useMemo(() => getSalonTimezone(currentSalon), [currentSalon]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [coachingOpen, setCoachingOpen] = useState(false);
  const [trainingOpen, setTrainingOpen] = useState(false);
  const [coachingNote, setCoachingNote] = useState("");
  const [coachingReviewId, setCoachingReviewId] = useState("");
  const [trainingModules, setTrainingModules] = useState([]);
  const [selectedModuleId, setSelectedModuleId] = useState("");
  const [trainingReviewId, setTrainingReviewId] = useState("");
  const [trainingNote, setTrainingNote] = useState("");
  const [saving, setSaving] = useState(false);

  const loadSummary = useCallback(async () => {
    if (!staffId) return;
    setLoading(true);
    setError("");
    try {
      const data = await getStaffClientSatisfaction(staffId, token);
      setSummary(data || null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load satisfaction data.");
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [staffId, token]);

  const loadModules = useCallback(async () => {
    try {
      const data = await listTrainingModules(token);
      setTrainingModules(Array.isArray(data) ? data : []);
    } catch (err) {
      setTrainingModules([]);
    }
  }, [token]);

  useEffect(() => {
    loadSummary();
    loadModules();
  }, [loadSummary, loadModules]);

  const recentFeedback = Array.isArray(summary?.recent_feedback)
    ? summary.recent_feedback
    : [];

  const handleSaveCoaching = async () => {
    if (!staffId || !coachingNote.trim()) return;
    setSaving(true);
    try {
      await createStaffCoachingNote(
        staffId,
        {
          note: coachingNote.trim(),
          review_request_id: coachingReviewId ? Number(coachingReviewId) : null,
        },
        token
      );
      setCoachingNote("");
      setCoachingReviewId("");
      setCoachingOpen(false);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to save coaching note.");
    } finally {
      setSaving(false);
    }
  };

  const handleAssignTraining = async () => {
    if (!staffId || !selectedModuleId || !trainingReviewId) return;
    setSaving(true);
    try {
      await assignTrainingFromFeedback(
        staffId,
        {
          review_request_id: Number(trainingReviewId),
          module_id: Number(selectedModuleId),
          note: trainingNote.trim() || null,
        },
        token
      );
      setTrainingOpen(false);
      setTrainingNote("");
      setSelectedModuleId("");
      setTrainingReviewId("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to assign training.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={cardClass}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg md:text-xl font-semibold text-slate-900">
            Client Satisfaction
          </h3>
          <p className="text-sm md:text-base text-slate-500">
            Recent ratings and feedback for this staff member.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setCoachingOpen(true)}
            className="rounded-full border border-slate-300 px-4 py-2 text-xs md:text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Create coaching note
          </button>
          {trainingModules.length > 0 && (
            <button
              type="button"
              onClick={() => setTrainingOpen(true)}
              className="rounded-full bg-rose-500 px-4 py-2 text-xs md:text-sm font-semibold text-white hover:bg-rose-600"
            >
              Assign training module
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </div>
      )}
      {loading && (
        <div className="mt-4 text-sm text-slate-500">Loading data...</div>
      )}

      {!loading && summary && (
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Avg rating (30d)
              </div>
              <div className="mt-2 text-2xl font-semibold text-slate-900">
                {summary.avg_rating_30_days.toFixed(2)}
              </div>
              <div className="text-xs text-slate-500">
                {summary.feedback_count_30_days} reviews
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Avg rating (90d)
              </div>
              <div className="mt-2 text-2xl font-semibold text-slate-900">
                {summary.avg_rating_90_days.toFixed(2)}
              </div>
              <div className="text-xs text-slate-500">
                {summary.feedback_count_90_days} reviews
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Open follow-ups
              </div>
              <div className="mt-2 text-2xl font-semibold text-slate-900">
                {summary.open_followups}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full table-fixed text-sm">
              <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left w-36">Submitted</th>
                  <th className="px-3 py-2 text-left w-32">Ticket</th>
                  <th className="px-3 py-2 text-left w-20">Rating</th>
                  <th className="px-3 py-2 text-left">Comment</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {recentFeedback.length === 0 && (
                  <tr>
                    <td className="px-3 py-4 text-slate-500" colSpan={4}>
                      No recent feedback.
                    </td>
                  </tr>
                )}
                {recentFeedback.map((row) => (
                  <tr key={row.review_request_id} className="hover:bg-slate-50">
                    <td className="px-3 py-3 text-slate-700">
                      {row.submitted_at
                        ? formatDateTimeInTimeZone(row.submitted_at, salonTimezone)
                        : "--"}
                    </td>
                    <td className="px-3 py-3 text-slate-700">
                      <a
                        href={`/tickets/${row.ticket_id}`}
                        className="font-semibold text-rose-600 hover:text-rose-500"
                      >
                        {row.ticket_number || `Ticket ${row.ticket_id}`}
                      </a>
                    </td>
                    <td className="px-3 py-3 text-slate-700">
                      {row.overall_rating ?? "--"}
                    </td>
                    <td className="px-3 py-3 text-slate-600">
                      {row.comment || "--"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SBINModal
        isOpen={coachingOpen}
        onClose={() => setCoachingOpen(false)}
        title="Create Coaching Note"
        size="lg"
        fitContent
        footer={
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setCoachingOpen(false)}
              className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveCoaching}
              disabled={saving}
              className="rounded-full bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save note"}
            </button>
          </div>
        }
      >
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase text-slate-500">
              Link to feedback (optional)
            </label>
            <select
              value={coachingReviewId}
              onChange={(event) => setCoachingReviewId(event.target.value)}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="">Not linked</option>
              {recentFeedback.map((row) => (
                <option key={row.review_request_id} value={row.review_request_id}>
                  {row.ticket_number || `Ticket ${row.ticket_id}`} • {row.overall_rating ?? "--"}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase text-slate-500">
              Note
            </label>
            <textarea
              value={coachingNote}
              onChange={(event) => setCoachingNote(event.target.value)}
              rows={4}
              className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm"
              placeholder="Add coaching note..."
            />
          </div>
        </div>
      </SBINModal>

      <SBINModal
        isOpen={trainingOpen}
        onClose={() => setTrainingOpen(false)}
        title="Assign Training Module"
        size="lg"
        fitContent
        footer={
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setTrainingOpen(false)}
              className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAssignTraining}
              disabled={saving || !selectedModuleId || !trainingReviewId}
              className="rounded-full bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
            >
              {saving ? "Assigning..." : "Assign"}
            </button>
          </div>
        }
      >
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase text-slate-500">
              Feedback reference
            </label>
            <select
              value={trainingReviewId}
              onChange={(event) => setTrainingReviewId(event.target.value)}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="">Select feedback</option>
              {recentFeedback.map((row) => (
                <option key={row.review_request_id} value={row.review_request_id}>
                  {row.ticket_number || `Ticket ${row.ticket_id}`} • {row.overall_rating ?? "--"}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase text-slate-500">
              Training module
            </label>
            <select
              value={selectedModuleId}
              onChange={(event) => setSelectedModuleId(event.target.value)}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="">Select module</option>
              {trainingModules.map((module) => (
                <option key={module.id} value={module.id}>
                  {module.title || module.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase text-slate-500">
              Note (optional)
            </label>
            <textarea
              value={trainingNote}
              onChange={(event) => setTrainingNote(event.target.value)}
              rows={3}
              className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm"
            />
          </div>
        </div>
      </SBINModal>
    </div>
  );
}
